import { useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import PageHeader from "../../component/pageHeaders";
import {
  Button,
  Checkbox,
  DatePicker,
  Modal,
  Progress,
  Spin,
  Tag,
  message,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import PagesList from "./component/pagesList";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { API } from "../../config/API";
import { POST } from "../../utils/apirequest";
import editons from "./component/editions.json";
import { convertApi } from "./helpers/compressUpload";
import { useSelector } from "react-redux";

function CommonPagesUpload() {
  const navigation = useNavigate();
  const User = useSelector((state: any) => state.User.user);

  const [date, setDate] = useState<dayjs.Dayjs>(dayjs());
  const [selectedEditions, setSelectedEditions] = useState<string[]>([]);
  const [pages, setPages] = useState<any[]>([]);
  const [currentUploadIndex, setCurrentUploadIndex] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    uploading: false,
    current: 0,
    total: 0,
  });
  const uploadCancelledRef = useRef(false);
  const [resultModal, setResultModal] = useState<any>(null);

  const toggleEdition = (edition: string) => {
    setSelectedEditions((prev) =>
      prev.includes(edition)
        ? prev.filter((e) => e !== edition)
        : [...prev, edition],
    );
  };

  const selectAll = () => setSelectedEditions([...editons]);
  const clearAll = () => setSelectedEditions([]);

  const uploadPages = async (pages: any[]) => {
    try {
      uploadCancelledRef.current = false;
      const totalToUpload = pages.filter(
        (p: any) => p.uploaded === false,
      ).length;
      let uploadedCount = 0;
      if (totalToUpload > 0) {
        setUploadProgress({
          uploading: true,
          current: 0,
          total: totalToUpload,
        });
      }

      const arro: any[] = [...pages];
      const currentDate = new Date();
      currentDate.setHours(currentDate.getHours() + 6);

      for (let i = 0; i < arro.length; i++) {
        if (uploadCancelledRef.current) break;
        if (arro[i].uploaded === false) {
          uploadedCount++;
          setUploadProgress({
            uploading: true,
            current: uploadedCount,
            total: totalToUpload,
          });
          setCurrentUploadIndex(i);
          const file = arro[i]?.file;
          const up: any = await convertApi(
            file,
            "page",
            i,
            4,
            "common",
            currentDate,
          );
          if (uploadCancelledRef.current) break;
          arro[i] = {
            index: arro[i].index,
            imageUrl: up?.image_url,
            image: up?.image_name,
            name: arro[i]?.name,
            uploaded: true,
            server: "scaleway",
          };
          setPages([...arro]);
          setCurrentUploadIndex(null);
        }
      }
      setUploadProgress({ uploading: false, current: 0, total: 0 });
      if (uploadCancelledRef.current) return null;
      return arro;
    } catch (err) {
      console.log("upload error:", err);
      setUploadProgress({ uploading: false, current: 0, total: 0 });
      return [];
    }
  };

  const handleApply = async () => {
    if (!selectedEditions.length) {
      message.error("Please select at least one edition");
      return;
    }
    if (!pages.length) {
      message.error("Please upload at least one page");
      return;
    }

    setLoading(true);

    // Phase 1: Upload files to external server
    const uploadedPages = await uploadPages(pages);
    if (uploadedPages === null) {
      setLoading(false);
      message.info("Upload cancelled");
      return;
    }

    // Phase 2: Distribute to editions via backend
    try {
      const body = {
        editions: selectedEditions,
        pages: uploadedPages,
        date: date.format("YYYY-MM-DD"),
        userId: User?.id,
      };
      const res: any = await POST(API.EPAPER_COMMON_PAGES, body);
      setLoading(false);
      if (res?.status) {
        setResultModal(res.results);
        message.success("Common pages applied successfully");
      } else {
        message.error(res?.message || "Failed to apply common pages");
      }
    } catch (err) {
      setLoading(false);
      console.log("apply error:", err);
      message.error("Failed to apply common pages");
    }
  };

  return (
    <div style={{ height: "95vh", overflowY: "scroll" }}>
      <PageHeader
        title={"E-Paper"}
        length={"Common Pages Upload"}
        onBack={() => navigation("/Auth/Epaper")}
      >
        <Tag style={{ fontSize: 14 }}>{date.format("DD-MM-YYYY")}</Tag>
      </PageHeader>

      <Container>
        <br />
        <Row>
          <Col sm={4}>
            <label className="newsFormLabels">Date</label>
            <DatePicker
              value={date}
              format="DD-MM-YYYY"
              size="large"
              style={{ width: "100%", marginBottom: 16 }}
              onChange={(value) => {
                if (value) setDate(value);
              }}
            />

            <label className="newsFormLabels">Select Editions</label>
            <div style={{ marginBottom: 8 }}>
              <Button
                size="small"
                onClick={selectAll}
                style={{ marginRight: 8 }}
              >
                Select All
              </Button>
              <Button size="small" onClick={clearAll}>
                Clear All
              </Button>
            </div>

            <div
              style={{
                maxHeight: 400,
                overflowY: "auto",
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                padding: 12,
              }}
            >
              {editons.map((edition: string) => (
                <div key={edition} style={{ marginBottom: 8 }}>
                  <Checkbox
                    checked={selectedEditions.includes(edition)}
                    onChange={() => toggleEdition(edition)}
                  >
                    {edition}
                  </Checkbox>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 12, color: "#888" }}>
              {selectedEditions.length} edition(s) selected
            </div>
          </Col>

          <Col sm={8}>
            <PagesList
              pages={pages}
              edition={"common"}
              date={date?.toDate()}
              uploadIndx={currentUploadIndex}
              onChagnge={(value: any) => setPages(value)}
            />
          </Col>
        </Row>

        <hr />
        <Row>
          <Col sm={4}></Col>
          <Col sm={8}>
            <Button
              block
              type="primary"
              size="large"
              style={{ height: 50 }}
              disabled={!selectedEditions.length || !pages.length || loading}
              onClick={handleApply}
            >
              Apply to {selectedEditions.length} Edition(s)
            </Button>
          </Col>
        </Row>

        {/* Upload Progress Modal */}
        <Modal
          open={uploadProgress.uploading}
          footer={null}
          closable={false}
          centered
          maskClosable={false}
          width={400}
        >
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />}
            />
            <h3 style={{ marginTop: 20, marginBottom: 8 }}>
              Uploading Pages...
            </h3>
            <p style={{ color: "#666", marginBottom: 16 }}>
              Uploading page {uploadProgress.current} of {uploadProgress.total}
            </p>
            <Progress
              percent={
                uploadProgress.total > 0
                  ? Math.round(
                      (uploadProgress.current / uploadProgress.total) * 100,
                    )
                  : 0
              }
              status="active"
            />
            <p style={{ color: "#999", fontSize: 12, marginTop: 12 }}>
              Please do not close or navigate away from this page.
            </p>
            <Button
              danger
              style={{ marginTop: 16 }}
              onClick={() => {
                uploadCancelledRef.current = true;
                setUploadProgress({
                  uploading: false,
                  current: 0,
                  total: 0,
                });
                setLoading(false);
                setCurrentUploadIndex(null);
                message.info("Upload cancelled");
              }}
            >
              Cancel Upload
            </Button>
          </div>
        </Modal>

        {/* Saving Modal */}
        <Modal
          open={loading && !uploadProgress.uploading}
          footer={null}
          closable={false}
          centered
          maskClosable={false}
          width={360}
        >
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />}
            />
            <h3 style={{ marginTop: 20, marginBottom: 0 }}>
              Applying to editions...
            </h3>
          </div>
        </Modal>

        {/* Result Modal */}
        <Modal
          open={!!resultModal}
          title="Common Pages Applied"
          onCancel={() => setResultModal(null)}
          footer={[
            <Button key="close" onClick={() => setResultModal(null)}>
              Close
            </Button>,
            <Button
              key="goto"
              type="primary"
              onClick={() => {
                setResultModal(null);
                navigation(`/Auth/Epaper?date=${date.format("YYYY-MM-DD")}`);
              }}
            >
              Go to E-Paper List
            </Button>,
          ]}
          centered
          width={500}
        >
          {resultModal && (
            <div style={{ padding: "12px 0" }}>
              {resultModal.created?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <strong>Created:</strong>
                  <div style={{ marginTop: 4 }}>
                    {resultModal.created.map((e: string) => (
                      <Tag key={e} color="blue" style={{ marginBottom: 4 }}>
                        {e}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
              {resultModal.updated?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <strong>Updated:</strong>
                  <div style={{ marginTop: 4 }}>
                    {resultModal.updated.map((e: string) => (
                      <Tag key={e} color="green" style={{ marginBottom: 4 }}>
                        {e}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
              {resultModal.failed?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <strong>Failed:</strong>
                  <div style={{ marginTop: 4 }}>
                    {resultModal.failed.map((e: string) => (
                      <Tag key={e} color="red" style={{ marginBottom: 4 }}>
                        {e}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>

        <br />
        <br />
        <br />
      </Container>
    </div>
  );
}

export default CommonPagesUpload;
